/**
 * 날짜만 있는 값. Postgres 의 `date` 열과 같은 `YYYY-MM-DD` 문자열이다.
 *
 * Date 로 들고 다니지 않는다. Date 는 시각이라 시간대가 붙고, 시간대가 붙은 값을 날짜로
 * 견주면 같은 순간이 기계에 따라 다른 날이 된다. 자재명세의 유효기간과 발주시점은 달력의
 * 날이지 시각이 아니다.
 *
 * 문자열이라 사전순이 곧 시간순이다. 유효기간 판정이 그것을 그대로 쓴다.
 */

const DAY = 86_400_000;

/**
 * 계획이 쓰는 시간대. 주 단위가 월요일부터 일요일까지의 한국 달력이다.
 *
 * 서버 기본값에 맡기지 않는다. 서버가 UTC 로 뜨면 월요일 오전 아홉 시 이전의 편집이 지난
 * 주차로 판정된다.
 */
export const ZONE = 'Asia/Seoul';

const FORMAT = new Intl.DateTimeFormat('en-CA', {
  timeZone: ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** 그 순간이 한국 달력으로 어느 날인가. */
export const today = (now: Date = new Date()): string => FORMAT.format(now);

/**
 * UTC 자정으로 선 Date 를 날짜로 읽는다. `iso-week.ts` 가 내는 Date 가 그것이다.
 *
 * 시간대를 보지 않는다. 그 Date 는 순간이 아니라 달력의 날을 담는 그릇이라, 지역 시간대로
 * 읽으면 하루가 밀린다.
 */
export const localDateOf = (date: Date): string => date.toISOString().slice(0, 10);

/** 날짜를 UTC 자정의 Date 로 되돌린다. 주차 계산이 그 모양을 받는다. */
export const toDate = (localDate: string): Date => {
  const date = new Date(`${localDate}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) throw new Error(`날짜 형식이 아니다: ${localDate}`);
  return date;
};

export const plusDays = (localDate: string, days: number): string =>
  localDateOf(new Date(toDate(localDate).getTime() + days * DAY));

export const minusDays = (localDate: string, days: number): string => plusDays(localDate, -days);

/** 한국 시각은 UTC 보다 아홉 시간 앞이고 일 년 내내 그렇다. 서머타임이 없어 상수로 둔다. */
const OFFSET_MS = 9 * 60 * 60 * 1_000;

const MINUTE_MS = 60_000;
const DAY_MINUTES = 24 * 60;

/**
 * 다음 [hhmm](한국 시각)까지 남은 밀리초. 지금이 딱 그 시각이면 내일 것을 가리킨다.
 *
 * 날마다 도는 배치가 쓴다. 시간대를 시스템에 맡기지 않는 것은, 서버가 UTC 로 기동하면 같은
 * 설정이 한국 시각 오후 한 시에 깨어나기 때문이다. 회차를 정하는 시계와 깨어나는 시계가 같은
 * 시간대여야 둘이 맞물린다.
 */
export const millisUntil = (hhmm: string, now: Date = new Date()): number => {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(hhmm);
  if (match === null) throw new Error(`시각은 HH:MM 이어야 한다: ${hhmm}`);
  const target = Number(match[1]) * 60 + Number(match[2]);
  // 한국의 벽시계를 그대로 읽으려고 시차만큼 민다.
  const shifted = now.getTime() + OFFSET_MS;
  const minutesOfDay = Math.floor(shifted / MINUTE_MS) % DAY_MINUTES;
  const deltaMinutes = target > minutesOfDay ? target - minutesOfDay : target - minutesOfDay + DAY_MINUTES;
  return deltaMinutes * MINUTE_MS - (shifted % MINUTE_MS);
};
